/* Ways to Save — front-end behaviour (vanilla ES2020, no jQuery; loaded deferred).
   Three independent modules, each a no-op when its markup is absent:
     1. Sticky-nav offset — keeps the sub-nav clear of the site header.
     2. Tabs              — builds the sub-nav from the panels, WAI-ARIA tabs
                            keyboard pattern, and lazy-loads a Calendar-grid panel.
     3. Events & Deals    — live theshopcalendar data, re-rendered in our markup. */

/* --------------------------------------------------------------------------
   1. Sticky sub-nav offset. --wts-sticky-top = the lowest bottom edge of the
   pinned header pieces, refreshed on scroll/resize (rAF-throttled). The CSS uses
   it as the sub-nav's sticky `top`, falling back to 0.
   -------------------------------------------------------------------------- */
(() => {
  if (!document.querySelector('.wts-subnav')) return;

  const els = [
    document.getElementById('SiteHeader'),
    ...document.querySelectorAll('[id*="free-shipping-bar"]'),
  ].filter(Boolean);
  if (!els.length) return;

  let raf = null;
  const update = () => {
    let max = 0;
    for (const el of els) {
      const pos = getComputedStyle(el).position;
      if (pos === 'fixed' || pos === 'sticky') max = Math.max(max, el.getBoundingClientRect().bottom);
    }
    document.documentElement.style.setProperty('--wts-sticky-top', `${Math.max(0, Math.round(max))}px`);
  };
  const onScroll = () => { if (!raf) raf = requestAnimationFrame(() => { raf = null; update(); }); };

  update();
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll, { passive: true });
  if ('ResizeObserver' in window) els.forEach((el) => new ResizeObserver(update).observe(el));
})();

/* --------------------------------------------------------------------------
   2. Tabs — sub-nav built from the panels; WAI-ARIA tabs pattern.

   Each panel's data-wts-tab-label becomes a tab (so the nav is fully driven by
   the blocks). Roving tabindex + arrow / Home / End keys move between tabs and
   activate them; click activates. A Calendar-grid panel embeds theshopcalendar's
   native month widget the first time it is shown (correct sizing, no wasted load)
   with a spinner while it and each month change process.
   -------------------------------------------------------------------------- */
(() => {
  document.querySelectorAll('.wts-tabs').forEach((tabs) => {
    const navInner = tabs.querySelector('.wts-subnav__inner');
    const panels = [...tabs.querySelectorAll('.wts-tab')];
    if (!navInner || !panels.length) return;

    // A tab's "anchor" (data-wts-tab-anchor, e.g. "events") makes it deep-linkable:
    // …/ways-to-save#events opens it, and clicking a tab reflects its anchor in
    // the URL. Tabs without an anchor keep their internal #panel-id href.
    const anchors = panels.map((p) => (p.dataset.wtsTabAnchor || '').toLowerCase());
    const idxFromHash = () => {
      const h = (location.hash || '').replace(/^#/, '').toLowerCase();
      return h ? anchors.findIndex((a) => a && a === h) : -1;
    };
    const syncHash = (i) => {
      if (anchors[i]) history.replaceState(null, '', `#${anchors[i]}`);
    };

    let activeIdx = Math.max(0, panels.findIndex((p) => p.dataset.wtsTabActive === 'true'));

    const links = panels.map((panel, i) => {
      const tabId = `${panel.id}-tab`;
      panel.setAttribute('aria-labelledby', tabId);

      const link = document.createElement('a');
      link.className = 'wts-subnav__link';
      link.id = tabId;
      link.href = anchors[i] ? `#${anchors[i]}` : `#${panel.id}`;
      link.textContent = panel.dataset.wtsTabLabel || `Tab ${i + 1}`;
      link.setAttribute('role', 'tab');
      link.setAttribute('aria-controls', panel.id);
      link.addEventListener('click', (e) => { e.preventDefault(); activate(i, true); syncHash(i); });
      link.addEventListener('keydown', onKeydown);
      navInner.appendChild(link);
      return link;
    });

    const activate = (i, focus) => {
      panels.forEach((p, j) => {
        const on = j === i;
        p.classList.toggle('is-active', on);
        p.hidden = !on;
        if (on) initCalendar(p);
      });
      links.forEach((l, j) => {
        const on = j === i;
        l.classList.toggle('wts-subnav__link--active', on);
        l.setAttribute('aria-selected', on ? 'true' : 'false');
        l.tabIndex = on ? 0 : -1;
      });
      activeIdx = i;
      if (focus) links[i].focus();
    };

    function onKeydown(e) {
      const last = links.length - 1;
      let next;
      switch (e.key) {
        case 'ArrowRight': case 'ArrowDown': next = activeIdx === last ? 0 : activeIdx + 1; break;
        case 'ArrowLeft': case 'ArrowUp': next = activeIdx === 0 ? last : activeIdx - 1; break;
        case 'Home': next = 0; break;
        case 'End': next = last; break;
        default: return;
      }
      e.preventDefault();
      activate(next, true);
    }

    tabs.classList.add('is-enhanced');

    // Back/forward and pasted #hash links switch the tab without a reload.
    addEventListener('hashchange', () => {
      const i = idxFromHash();
      if (i >= 0 && i !== activeIdx) activate(i, false);
    });

    // On load, a matching #hash wins over the default "active" tab and brings the
    // tabs into view (offset past the pinned header) so the deep link is visible.
    const hashIdx = idxFromHash();
    if (hashIdx >= 0) activeIdx = hashIdx;
    activate(activeIdx, false);

    if (hashIdx >= 0) {
      requestAnimationFrame(() => {
        const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--wts-sticky-top'), 10) || 0;
        const top = tabs.getBoundingClientRect().top + scrollY - offset - 8;
        scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    }
  });

  const startLoading = (host) => { host.classList.add('is-loading'); host.setAttribute('aria-busy', 'true'); };
  const stopLoading = (host) => { host.classList.remove('is-loading'); host.removeAttribute('aria-busy'); };

  function initCalendar(panel) {
    const host = panel.querySelector('.wts-cal[data-wts-cal-uuid]');
    if (!host || host.dataset.wtsCalInit) return;
    host.dataset.wtsCalInit = '1';

    const { wtsCalUuid: uuid } = host.dataset;
    startLoading(host);
    let timer = setTimeout(() => stopLoading(host), 15000);

    const mount = document.createElement('div');
    mount.id = `theshopcalendar-widget-${uuid}`;
    host.appendChild(mount);

    const script = document.createElement('script');
    script.className = 'calendar-widget';
    script.dataset.calendarUuid = uuid;
    script.src = 'https://www.theshopcalendar.com/widget.js';
    host.appendChild(script);

    // Prev/next arrows make the widget re-fetch + repaint the mount; show the
    // spinner from the click until the new grid renders. Delegated on host so it
    // survives the mount being replaced on each navigation.
    host.addEventListener('click', (e) => {
      if (!e.target.closest?.('.previous-month-btn, .next-month-btn')) return;
      startLoading(host);
      clearTimeout(timer);
      timer = setTimeout(() => stopLoading(host), 15000);
    });

    if ('MutationObserver' in window) {
      new MutationObserver(() => {
        if (host.classList.contains('is-loading') && mount.querySelector('.tscCalendar, .calendar')) {
          stopLoading(host);
          clearTimeout(timer);
        }
      }).observe(mount, { childList: true, subtree: true });
    }
  }
})();

/* --------------------------------------------------------------------------
   3. Events & Deals list — live data from theshopcalendar, our own markup.

   Each .wts-list__group[data-wts-source] names a calendar UUID and a mode
   ('accordion' events, 'list' deals). We fetch that calendar's embed endpoint
   (JSON {html}), parse it OFF-DOM (DOMParser runs no scripts), pull each event's
   date/time/title/description/link and rebuild .wts-list__row nodes — injecting
   only extracted text + hrefs, never the widget's raw HTML (no XSS surface, no
   widget UI). Fetches are cached by uuid+mode so a shared calendar loads once;
   the group's spinner is replaced with rows, or the group is removed when empty.
   -------------------------------------------------------------------------- */
(() => {
  const groups = [...document.querySelectorAll('.wts-list__group[data-wts-source]')];
  if (!groups.length) return;

  const BASE = 'https://www.theshopcalendar.com';
  const cache = new Map();

  const load = (uuid, mode) => {
    const key = `${uuid}|${mode}`;
    if (!cache.has(key)) {
      const path = mode === 'accordion' ? '/embed/cal/accordion/js_widget' : '/embed/cal/list/js_widget';
      cache.set(key, fetch(`${BASE}/${encodeURIComponent(uuid)}${path}`, { credentials: 'omit' })
        .then((r) => (r.ok ? r.json() : { html: '' }))
        .then((d) => d?.html ?? '')
        .catch(() => ''));
    }
    return cache.get(key);
  };

  const text = (el) => (el ? el.textContent.replace(/\s+/g, ' ').trim() : '');
  const attr = (el, name) => { const v = el?.getAttribute(name); return v ? v.replace(/\s+/g, ' ').trim() : ''; };
  const esc = (s) => { const d = document.createElement('div'); d.textContent = s ?? ''; return d.innerHTML; };
  const escAttr = (s) => esc(s).replace(/"/g, '&quot;');

  // Open external destinations in a new tab; keep internal links in-page.
  const extAttr = (url) => {
    try {
      const u = new URL(url, location.href);
      if ((u.protocol === 'http:' || u.protocol === 'https:') && u.host !== location.host) {
        return ' target="_blank" rel="noopener"';
      }
    } catch (e) { /* relative/hash → internal */ }
    return '';
  };

  const CLOCK = '<svg class="wts-list__clock" viewBox="0 0 24 24" fill="none" aria-hidden="true">'
    + '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>'
    + '<path d="M12 7v5l3 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  // Prefer a real destination over the widget's '#'/modal-only links.
  const realLink = (card) => {
    for (const a of card.querySelectorAll('a[href]')) {
      const h = a.getAttribute('href');
      if (h && h !== '#' && !h.startsWith('javascript')) return h;
    }
    return '';
  };

  const parseList = (doc) => [...doc.querySelectorAll('.tscListCard')].map((c) => ({
    date: text(c.querySelector('.tscListCardDate')),
    time: text(c.querySelector('.tscListCardEventDetailsTime')),
    title: text(c.querySelector('.tscListCardEventDetailsTitle')),
    desc: text(c.querySelector('.tscListCardEventDetailsDescription')),
    url: realLink(c),
  })).filter((r) => r.title);

  // Deals "image cards": same list data, plus each card's promo banner and the
  // month it falls under (from the sibling .tscMonthHeader that precedes it).
  const parseCards = (doc) => [...doc.querySelectorAll('.tscListCard')].map((c) => {
    const img = c.querySelector('.tscListCardImage img');
    let month = '', el = c;
    while ((el = el.previousElementSibling)) {
      if (el.classList?.contains('tscMonthHeader')) { month = text(el); break; }
    }
    return {
      month,
      image: img ? (img.getAttribute('src') || '') : '',
      time: text(c.querySelector('.tscListCardEventDetailsTime')),
      title: text(c.querySelector('.tscListCardEventDetailsTitle')),
      desc: text(c.querySelector('.tscListCardEventDetailsDescription')),
      url: realLink(c),
    };
  }).filter((r) => r.title);

  const parseAccordion = (doc) => [...doc.querySelectorAll('.tscAccordionCard')].map((c) => {
    const dt = c.querySelector('.tscAccordionDateAndTime');
    return {
      date: dt ? text(dt.querySelector('h4')) : '',
      time: dt ? text(dt.querySelector('h6')) : '',
      title: text(c.querySelector('.tscAccordionTitleAndLocation')) || text(c.querySelector('.tscAccordionTitle')),
      desc: attr(c, 'data-event-description'),
      url: realLink(c),
    };
  }).filter((r) => r.title);

  const rowHtml = (r, btnLabel) => {
    let when = `<span class="wts-list__day">${esc(r.date)}</span>`;
    if (r.time) when += `<span class="wts-list__time">${CLOCK}${esc(r.time)}</span>`;

    const title = r.url
      ? `<a class="wts-list__title" href="${escAttr(r.url)}"${extAttr(r.url)}>${esc(r.title)}</a>`
      : `<span class="wts-list__title">${esc(r.title)}</span>`;
    const desc = r.desc ? `<div class="wts-list__desc">${esc(r.desc)}</div>` : '';
    const action = r.url
      ? `<div class="wts-list__action"><a class="wts-btn" href="${escAttr(r.url)}"${extAttr(r.url)} aria-label="${escAttr(`${btnLabel}: ${r.title}`)}">${esc(btnLabel)}</a></div>`
      : '';

    return `<article class="wts-list__row"><div class="wts-list__when">${when}</div>`
      + `<div class="wts-list__body">${title}${desc}</div>${action}</article>`;
  };

  // A single deal rendered as a card: promo banner on top, then time, title and
  // description stacked. The banner links to the deal but is hidden from the
  // a11y tree (tabindex -1) so the title stays the only tab stop for the card.
  const dealCardHtml = (r, showClock) => {
    const img = r.image ? `<img src="${escAttr(r.image)}" alt="" loading="lazy">` : '';
    const media = !img ? ''
      : r.url ? `<a class="wts-deal__media" href="${escAttr(r.url)}"${extAttr(r.url)} tabindex="-1" aria-hidden="true">${img}</a>`
      : `<div class="wts-deal__media">${img}</div>`;
    const time = r.time ? `<div class="wts-deal__time">${showClock ? CLOCK : ''}${esc(r.time)}</div>` : '';
    const title = r.url
      ? `<a class="wts-deal__title" href="${escAttr(r.url)}"${extAttr(r.url)}>${esc(r.title)}</a>`
      : `<span class="wts-deal__title">${esc(r.title)}</span>`;
    const desc = r.desc ? `<div class="wts-deal__desc">${esc(r.desc)}</div>` : '';
    return `<article class="wts-deal">${media}<div class="wts-deal__body">${time}${title}${desc}</div></article>`;
  };

  // Deals grouped under their month heading (e.g. "July 2026").
  const cardsHtml = (rows, showClock) => {
    let out = '', lastMonth = null;
    for (const r of rows) {
      if (r.month && r.month !== lastMonth) {
        out += `<h2 class="wts-deals__month">${esc(r.month)}</h2>`;
        lastMonth = r.month;
      }
      out += dealCardHtml(r, showClock);
    }
    return out;
  };

  const fill = (group, html, mode) => {
    const { wtsHeading: heading, wtsButton: btnLabel = 'View Link' } = group.dataset;
    const limit = parseInt(group.dataset.wtsLimit || '0', 10);

    const doc = new DOMParser().parseFromString(html, 'text/html');
    let rows = mode === 'accordion' ? parseAccordion(doc)
      : mode === 'cards' ? parseCards(doc)
      : parseList(doc);
    if (limit > 0) rows = rows.slice(0, limit);

    if (!rows.length) { group.remove(); return; }

    if (mode === 'cards') {
      const showClock = group.dataset.wtsClock !== '0';
      const radius = parseInt(group.dataset.wtsImgRadius || '0', 10);
      group.style.setProperty('--wts-deal-radius', `${radius}px`);
      group.innerHTML = cardsHtml(rows, showClock);
    } else {
      const head = heading ? `<h2 class="wts-list__head">${esc(heading)}</h2>` : '';
      group.innerHTML = head + rows.map((r) => rowHtml(r, btnLabel)).join('');
    }
    group.removeAttribute('aria-busy');
  };

  groups.forEach((group) => {
    const modeAttr = group.dataset.wtsMode;
    const render = modeAttr === 'accordion' ? 'accordion' : modeAttr === 'cards' ? 'cards' : 'list';
    const fetchMode = render === 'accordion' ? 'accordion' : 'list';
    load(group.dataset.wtsSource, fetchMode).then((html) => fill(group, html, render));
  });
})();
